import { useState, useCallback, useRef, useEffect } from 'react';

interface Position {
    x: string | number;
    y: string | number;
}

interface DragPosition {
    x: number;
    y: number;
}

interface DraggableResult {
    position: Position;
    isDragging: boolean;
    dragPosition: DragPosition;
    elementRef: React.RefObject<HTMLDivElement>;
    handleMouseDown: (e: React.MouseEvent) => void;
}

/**
 * Custom hook to handle draggable element behavior
 * @param isMinimized - Whether the component is minimized
 * @returns - Drag handlers and state
 */
export const useDraggable = (isMinimized: boolean): DraggableResult => {
    const [position, setPosition] = useState<Position>({
        x: 'end-0',
        y: 'top-0',
    });
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [dragOffset, setDragOffset] = useState<DragPosition>({ x: 0, y: 0 });
    const [dragPosition, setDragPosition] = useState<DragPosition>({
        x: 0,
        y: 0,
    });
    const elementRef = useRef<HTMLDivElement>(null);

    // Start dragging the element
    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            if (!isMinimized) return;

            const element = elementRef.current;
            if (!element) return;

            // Calculate offset from mouse position to element corner
            const rect = element.getBoundingClientRect();
            const offsetX = e.clientX - rect.left;
            const offsetY = e.clientY - rect.top;

            setIsDragging(true);
            setDragOffset({ x: offsetX, y: offsetY });
            setDragPosition({ x: rect.left, y: rect.top });

            // Prevent text selection during drag
            e.preventDefault();
        },
        [isMinimized],
    );

    // Move the element during drag
    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isDragging) return;

            // Calculate new position based on mouse position and initial offset
            const x = e.clientX - dragOffset.x;
            const y = e.clientY - dragOffset.y;

            // Update element position
            setDragPosition({ x, y });
        },
        [isDragging, dragOffset],
    );

    // End the dragging operation
    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            // Transition from fixed positioning to tailwind classes
            // Based on the final position, determine which corner to stick to
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;

            // Determine horizontal position (left, center, right)
            let xPosition = 'end-0';
            if (dragPosition.x < windowWidth / 3) {
                xPosition = 'start-0';
            } else if (dragPosition.x < (windowWidth * 2) / 3) {
                xPosition = 'start-1/2 -translate-x-1/2';
            }

            // Determine vertical position (top, middle, bottom)
            let yPosition = 'top-0';
            if (dragPosition.y > (windowHeight * 2) / 3) {
                yPosition = 'bottom-0';
            } else if (dragPosition.y > windowHeight / 3) {
                yPosition = 'top-1/2 -translate-y-1/2';
            }

            setPosition({ x: xPosition, y: yPosition });
            setIsDragging(false);
        }
    }, [isDragging, dragPosition]);

    // Add and remove event listeners for drag operations
    useEffect(() => {
        if (isMinimized) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isMinimized, handleMouseMove, handleMouseUp]);

    return {
        position,
        isDragging,
        dragPosition,
        elementRef,
        handleMouseDown,
    };
};
